import { useState, useRef, useEffect, useCallback } from "react";
import { Bot, X, Send, ArrowLeft, Loader2, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";

type Msg = { role: "user" | "assistant"; content: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/feed-recommendation`;

const birdTypes = ["Broiler", "Layer", "Breeder", "Other"];
const ageRanges = ["0–2 Weeks", "2–4 Weeks", "5+ Weeks", "Mixed Ages"];
const goals = ["Maximum Growth", "Better FCR", "Improved Meat Quality", "Cost Optimization", "Health & Immunity"];

async function streamChat({
  body,
  onDelta,
  onDone,
  onError,
}: {
  body: Record<string, unknown>;
  onDelta: (t: string) => void;
  onDone: () => void;
  onError: (msg: string) => void;
}) {
  const resp = await fetch(CHAT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: "Request failed" }));
    onError(err.error || "Something went wrong");
    return;
  }
  if (!resp.body) { onError("No response body"); return; }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    let idx: number;
    while ((idx = buf.indexOf("\n")) !== -1) {
      let line = buf.slice(0, idx);
      buf = buf.slice(idx + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (!line.startsWith("data: ")) continue;
      const json = line.slice(6).trim();
      if (json === "[DONE]") { onDone(); return; }
      try {
        const parsed = JSON.parse(json);
        const c = parsed.choices?.[0]?.delta?.content;
        if (c) onDelta(c);
      } catch {
        buf = line + "\n" + buf;
        break;
      }
    }
  }
  onDone();
}

const FeedRecommendationWidget = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"menu" | "form" | "chat">("menu");
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Form state
  const [birdType, setBirdType] = useState("");
  const [age, setAge] = useState("");
  const [flockSize, setFlockSize] = useState("");
  const [goal, setGoal] = useState("");

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const appendAssistant = useCallback((chunk: string) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === "assistant") {
        return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: m.content + chunk } : m);
      }
      return [...prev, { role: "assistant", content: chunk }];
    });
  }, []);

  const flockNum = parseInt(flockSize, 10);
  const isFlockValid = !isNaN(flockNum) && flockNum > 0;

  const handleQuickForm = async () => {
    if (!birdType || !age || !goal || !isFlockValid) return;
    setView("chat");
    setMessages([{ role: "user", content: `Bird: ${birdType} | Age: ${age} | Flock: ${flockNum} birds | Goal: ${goal}` }]);
    setLoading(true);
    await streamChat({
      body: { quickForm: { birdType, age, flockSize: String(flockNum), goal } },
      onDelta: appendAssistant,
      onDone: () => setLoading(false),
      onError: (msg) => {
        appendAssistant(`⚠️ ${msg}`);
        setLoading(false);
      },
    });
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    const userMsg: Msg = { role: "user", content: text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setLoading(true);
    await streamChat({
      body: { messages: updated },
      onDelta: appendAssistant,
      onDone: () => setLoading(false),
      onError: (msg) => {
        appendAssistant(`⚠️ ${msg}`);
        setLoading(false);
      },
    });
  };

  const reset = () => {
    setView("menu");
    setMessages([]);
    setBirdType("");
    setAge("");
    setFlockSize("");
    setGoal("");
    setInput("");
  };

  return (
    <>
      {/* Trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 z-[60] w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
          aria-label="Feed Recommendation AI"
        >
          <Sparkles className="w-7 h-7" />
        </button>
      )}

      {/* Widget */}
      {open && (
        <div className="fixed bottom-6 left-4 right-4 z-[60] max-h-[70vh] rounded-2xl shadow-2xl border border-border bg-card flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300 md:bottom-8 md:left-8 md:right-auto md:w-[360px] md:max-h-[520px]">
          {/* Header */}
          <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center gap-3 shrink-0">
            {view !== "menu" && (
              <button onClick={reset} className="hover:opacity-80 transition-opacity">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Bot className="w-6 h-6" />
            <div className="flex-1">
              <p className="font-display font-bold text-sm">Feed Advisor AI</p>
              <p className="text-xs opacity-80 font-body">Powered by Sandhu Feeds</p>
            </div>
            <button onClick={() => setOpen(false)} className="hover:opacity-80 transition-opacity">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Menu */}
          {view === "menu" && (
            <div className="p-5 flex flex-col gap-4">
              <p className="text-sm text-muted-foreground font-body">
                Get personalized feed recommendations for your poultry farm.
              </p>
              <button
                onClick={() => setView("form")}
                className="w-full p-4 rounded-xl border border-border bg-secondary/30 hover:bg-secondary/60 transition-colors text-left"
              >
                <p className="font-display font-bold text-foreground text-sm">⚡ Quick Recommendation</p>
                <p className="text-xs text-muted-foreground font-body mt-1">Fill a short form & get instant results</p>
              </button>
              <button
                onClick={() => {
                  setView("chat");
                  setMessages([{ role: "assistant", content: "Hello! 👋 I'm the Sandhu Feeds advisor. Tell me about your poultry farm and I'll recommend the best feed for your birds!" }]);
                }}
                className="w-full p-4 rounded-xl border border-border bg-secondary/30 hover:bg-secondary/60 transition-colors text-left"
              >
                <p className="font-display font-bold text-foreground text-sm">💬 Chat with AI</p>
                <p className="text-xs text-muted-foreground font-body mt-1">Ask anything about poultry nutrition</p>
              </button>
            </div>
          )}

          {/* Quick Form */}
          {view === "form" && (
            <div className="p-4 flex flex-col gap-3 overflow-y-auto flex-1">
              <label className="text-xs font-semibold text-foreground font-body">Bird Type</label>
              <div className="flex flex-wrap gap-2">
                {birdTypes.map((b) => (
                  <button key={b} onClick={() => setBirdType(b)}
                    className={`px-3 py-1.5 rounded-full text-xs font-body border transition-colors ${birdType === b ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                    {b}
                  </button>
                ))}
              </div>

              <label className="text-xs font-semibold text-foreground font-body">Age</label>
              <div className="flex flex-wrap gap-2">
                {ageRanges.map((a) => (
                  <button key={a} onClick={() => setAge(a)}
                    className={`px-3 py-1.5 rounded-full text-xs font-body border transition-colors ${age === a ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                    {a}
                  </button>
                ))}
              </div>

              <label className="text-xs font-semibold text-foreground font-body">Flock Size</label>
              <input
                type="number"
                min={1}
                value={flockSize}
                onChange={(e) => setFlockSize(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="e.g. 500"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm font-body focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              {flockSize && !isFlockValid && (
                <p className="text-xs text-destructive font-body">Please enter a flock size greater than 0.</p>
              )}

              <label className="text-xs font-semibold text-foreground font-body">Primary Goal</label>
              <div className="flex flex-wrap gap-2">
                {goals.map((g) => (
                  <button key={g} onClick={() => setGoal(g)}
                    className={`px-3 py-1.5 rounded-full text-xs font-body border transition-colors ${goal === g ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                    {g}
                  </button>
                ))}
              </div>

              <button
                onClick={handleQuickForm}
                disabled={!birdType || !age || !isFlockValid || !goal}
                className="mt-2 w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-display font-bold text-sm hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Get Recommendation
              </button>
            </div>
          )}

          {/* Chat */}
          {view === "chat" && (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm font-body ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-md"
                        : "bg-secondary/50 text-foreground rounded-bl-md"
                    }`}>
                      {m.role === "assistant" ? (
                        <div className="prose prose-sm max-w-none [&_p]:my-1 [&_ul]:my-1 [&_li]:my-0.5 [&_strong]:text-foreground">
                          <ReactMarkdown>{m.content}</ReactMarkdown>
                        </div>
                      ) : m.content}
                    </div>
                  </div>
                ))}
                {loading && messages[messages.length - 1]?.role === "user" && (
                  <div className="flex justify-start">
                    <div className="bg-secondary/50 rounded-2xl rounded-bl-md px-4 py-3">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-3 border-t border-border shrink-0">
                <div className="flex gap-2">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    placeholder="Ask about feed..."
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm font-body focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!input.trim() || loading}
                    className="w-9 h-9 rounded-lg bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 disabled:opacity-40 transition-colors shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default FeedRecommendationWidget;
